import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import {
  CompetencyOutcomeItem,
  DiagnosticReport,
  EvaluationBand,
  GradeLevelRefItem,
  SubjectDiagnostic,
} from '../../../../core/interfaces/diagnostic-report';
import { ChartTheme, competencyColorMap, paletteFor } from './charts/chart-palette';
import { ReportI18nService } from './report-i18n.service';
import {
  AcquisitionView,
  BandRef,
  BandScaleSegment,
  ComparisonRow,
  CompetencyRow,
  CrossSubjectView,
  DonutSlice,
  GradeLevelRef,
  PlanWeek,
  ReportHeaderView,
  ReportLevel,
  ReportScope,
  ReportView,
  RingSpoke,
  SchoolLevelPoint,
  ScoreView,
  SubjectOption,
  SubjectReportView,
  SubjectRow,
  WeeklyBar,
} from './report.model';

/**
 * La note ramenée sur 20, barème des bulletins tunisiens.
 *
 * <p>La carte de synthèse affichait les points bruts — « 10 / 27 » — sous une
 * légende qui annonçait « sur 20 » : deux barèmes dans une même carte, dont un
 * seul écrit.</p>
 *
 * <p>Calculée sur les points et non sur le pourcentage : celui-ci arrive arrondi
 * à l'unité par l'API, et 37 % rendrait 7,40 là où 10 sur 27 vaut 7,41.</p>
 *
 * @returns <c>null</c> quand l'épreuve ne porte aucun point : il n'y a alors pas
 * de note, et zéro se lirait comme un échec.
 */
export function noteSur20(points: number, bareme: number): number | null {
  if (!(bareme > 0)) return null;
  return (points * 20) / bareme;
}

/**
 * Temps total passé sur les séances qu'un rapport couvre.
 *
 * <p>Somme des durées <b>mesurées</b>. Une séance sans mesure ne compte pas
 * pour zéro : elle ne compte pas du tout, et si aucune n'a été mesurée, il n'y
 * a rien à annoncer — l'en-tête fait alors disparaître l'item plutôt que
 * d'afficher un zéro qui se lirait comme un test non passé.</p>
 */
export function sommeDesDurees(
  seances: readonly { durationMinutes: number | null }[],
): number | null {
  const mesurees = seances
    .map((s) => s.durationMinutes)
    .filter((d): d is number => d != null && d > 0);

  if (mesurees.length === 0) return null;
  return mesurees.reduce((total, d) => total + d, 0);
}

/**
 * Construction du modèle de vue du rapport de diagnostic.
 *
 * Tout ce que le rapport affiche est calculé **ici, une fois**, puis rendu tel
 * quel par les sections. C'est le remplacement du script d'alimentation qui
 * réécrivait la maquette après coup (`../mocks/report/js/hydrate.js`) : celui-ci
 * retrouvait les compteurs par leur *valeur de démonstration* et passait des
 * libellés déjà traduits en guise de clés de traduction. Deux bricolages qui
 * cassaient au premier chiffre identique et au premier libellé qui ressemblait
 * à une clé. Rien de tout cela ne subsiste : le modèle de vue est typé, et
 * chaque valeur y arrive par son sens, pas par sa forme.
 *
 * ── L'unité est la matière ─────────────────────────────────────────────────
 * `subjects` porte un rapport complet **par matière** — une seule en portée
 * `subject`, toutes en portée `all` — et rien n'est jamais agrégé d'une
 * matière à l'autre : un élève peut être à l'aise en mathématiques et en
 * difficulté en arabe, et une moyenne de compétences toutes matières
 * confondues ne voudrait rien dire. Seul `crossSubject` compare, et il ne
 * compare que des scores.
 *
 * ── Fonction pure ──────────────────────────────────────────────────────────
 * Aucun appel réseau, aucun accès au DOM, aucune horloge : `generatedAt` vient
 * de la charge utile. Deux appels avec les mêmes arguments (à langue et thème
 * constants) rendent le même objet, ce qui rend la vue rejouable — et
 * testable — sans monter d'environnement.
 */
@Injectable({ providedIn: 'root' })
export class ReportViewBuilder {
  private readonly i18n = inject(ReportI18nService);
  private readonly translate = inject(TranslateService);

  /**
   * Modèle de vue complet, prêt à rendre.
   *
   * @param report            Charge utile de l'API, telle que reçue.
   * @param scope             Une matière, ou toutes.
   * @param selectedSubjectId Matière affichée en portée `subject` ; ignoré en
   *                          portée `all`.
   * @param theme             Clair ou sombre : les graphiques sont des
   *                          attributs SVG et ne suivent pas le thème seuls.
   */
  build(
    report: DiagnosticReport,
    scope: ReportScope,
    selectedSubjectId: number | null,
    theme: ChartTheme,
  ): ReportView {
    const payloadSubjects = safeList(report.subjects);
    const referential = safeList(report.gradeLevels);
    const bands = safeList(report.bands).map((band) => this.toBandRef(band));
    const planWeeks = normalizeWeekCount(report.planWeeks);
    const ranks = this.rankByGradeLevelCode(referential);

    const scoped = this.subjectsInScope(payloadSubjects, scope, selectedSubjectId);
    const subjects = scoped.map((subject) =>
      this.buildSubject(subject, bands, ranks, planWeeks, theme),
    );

    return {
      scope,
      // En portée `subject`, on renvoie l'identifiant **réellement affiché** :
      // il peut différer de celui demandé si celui-ci ne correspondait à
      // aucune matière (voir `subjectsInScope`). Renvoyer la demande plutôt
      // que le résultat ferait mentir la barre latérale.
      selectedSubjectId: scope === 'subject' ? (subjects[0]?.subjectId ?? null) : null,
      subjectOptions: this.buildSubjectOptions(report, payloadSubjects, bands),
      header: this.buildHeader(report, scope, subjects),
      subjects,
      crossSubject:
        scope === 'all' ? this.buildCrossSubject(report, subjects) : null,
      planWeeks,
      gradeLevelScale: referential.map((level) => this.toGradeLevelRef(level)),
      generatedAt: report.generatedAt,
    };
  }

  /* ─────────────────────────────────────────────────────────────────────
     Portée
     ───────────────────────────────────────────────────────────────────── */

  /**
   * Matières couvertes par le document.
   *
   * En portée `all`, toutes, **dans l'ordre de l'API** : c'est l'ordre du
   * parcours de l'élève, et le retrier ici ferait diverger le rapport du
   * sélecteur. En portée `subject`, la seule matière demandée — et à défaut la
   * première : un identifiant périmé (matière désaffectée, lien partagé) doit
   * donner un rapport lisible plutôt qu'une page vide.
   */
  private subjectsInScope(
    subjects: readonly SubjectDiagnostic[],
    scope: ReportScope,
    selectedSubjectId: number | null,
  ): readonly SubjectDiagnostic[] {
    if (scope === 'all') return subjects;
    if (subjects.length === 0) return [];

    const match = subjects.find((s) => s.subjectId === selectedSubjectId);
    return [match ?? subjects[0]];
  }

  /* ─────────────────────────────────────────────────────────────────────
     Le rapport d'une matière
     ───────────────────────────────────────────────────────────────────── */

  private buildSubject(
    subject: SubjectDiagnostic,
    bands: readonly BandRef[],
    ranks: ReadonlyMap<string, number>,
    planWeeks: number,
    theme: ChartTheme,
  ): SubjectReportView {
    if (!subject.hasDiagnostic) return this.emptySubject(subject);

    const competencies = this.buildCompetencies(safeList(subject.competencies), theme);
    const gaps = competencies.filter((c) => c.outcome === 'NotAcquired');
    const strengths = competencies.filter((c) => c.outcome === 'Acquired');
    const band = this.resolveBand(subject, bands);

    return {
      subjectId: subject.subjectId,
      subjectName: subject.subjectName,
      hasDiagnostic: true,
      anchorId: anchorFor(subject.subjectId),

      testTitle: subject.testTitle ?? null,
      completedAt: subject.completedAt ?? null,
      durationMinutes: subject.durationMinutes ?? null,

      score: this.buildScore(subject, band),
      acquisition: this.buildAcquisition(subject),

      competencies,
      skillCodes: competencies.map((c) => c.shortCode),
      strengths,
      gaps,

      bandScale: this.buildBandScale(bands, subject.scorePercentage),
      plan: this.buildPlan(gaps, planWeeks),

      charts: {
        donut: this.buildDonut(subject, theme),
        ring: this.buildRing(gaps),
        weekly: this.buildWeekly(gaps),
        schoolLevel: this.buildSchoolLevel(competencies, ranks),
      },
    };
  }

  /**
   * Matière affectée mais pas encore diagnostiquée.
   *
   * Elle figure au rapport — c'est ce qu'il reste à faire, et une matière
   * absente ne le dirait pas — mais réduite à son en-tête : ni palier, ni
   * graphique, ni plan. Les compteurs sont à zéro parce que le modèle les
   * déclare obligatoires, et `hasDiagnostic` est là pour que la vue affiche un
   * état vide plutôt que ces zéros, qui sanctionneraient un test qui n'a pas
   * eu lieu. Aucune de ces valeurs n'entre dans un total d'ensemble.
   */
  private emptySubject(subject: SubjectDiagnostic): SubjectReportView {
    return {
      subjectId: subject.subjectId,
      subjectName: subject.subjectName,
      hasDiagnostic: false,
      anchorId: anchorFor(subject.subjectId),

      testTitle: null,
      completedAt: null,
      durationMinutes: null,

      score: {
        score: 0,
        maxScore: 0,
        markOutOf20: null,
        percentage: 0,
        target: subject.target ?? null,
        band: null,
        level: null,
        passed: false,
      },
      acquisition: {
        acquired: 0,
        notAcquired: 0,
        notAssessed: 0,
        total: 0,
        acquiredPercentage: 0,
        notAcquiredPercentage: 0,
        notAssessedPercentage: 0,
      },

      competencies: [],
      skillCodes: [],
      strengths: [],
      gaps: [],

      bandScale: [],
      plan: [],
      charts: { donut: [], ring: [], weekly: [], schoolLevel: [] },
    };
  }

  /* ─────────────────────────────────────────────────────────────────────
     Compétences et couleurs
     ───────────────────────────────────────────────────────────────────── */

  /**
   * Compétences d'une matière, numérotées et colorées.
   *
   * **La numérotation est propre à la matière** : les compétences reçoivent les
   * rangs 1..n dans l'ordre où l'API les renvoie, c'est-à-dire l'ordre
   * d'affichage du référentiel. Deux matières ont donc chacune leur `c1`, et
   * c'est sans ambiguïté : un panneau ne montre jamais qu'une matière, et
   * l'anneau, le plan et l'axe des compétences d'un même panneau parlent tous
   * de la même liste. Repartir d'une numérotation continue sur tout le rapport
   * donnerait des `c27` illisibles sur un axe et changerait le code d'une
   * compétence dès qu'une autre matière gagne une ligne.
   *
   * Les couleurs sont attribuées **positionnellement** sur cette même liste :
   * la n-ième compétence de la matière reçoit toujours la n-ième teinte, si
   * bien que `c1` garde sa couleur d'une section à l'autre et d'un rendu à
   * l'autre.
   */
  private buildCompetencies(
    items: readonly CompetencyOutcomeItem[],
    theme: ChartTheme,
  ): CompetencyRow[] {
    const shortCodes = items.map((_, index) => this.shortCode(index + 1));
    const colors = competencyColorMap(shortCodes, theme);

    return items.map((item, index) => ({
      competencyId: item.competencyId,
      code: item.code,
      displayIndex: index + 1,
      shortCode: shortCodes[index],
      title: item.title,
      // Le descripteur est obligatoire côté référentiel : c'est lui que le
      // rapport affiche. Le repli sur le titre ne couvre qu'une donnée
      // incomplète, et rend alors un libellé technique plutôt qu'une ligne
      // vide — le lecteur voit au moins de quelle compétence il s'agit.
      skillDescriptor: item.skillDescriptor?.trim() || item.title,
      themeTitle: item.themeTitle ?? null,
      gradeLevels: safeList(item.gradeLevels).map((level) => this.toGradeLevelRef(level)),
      prerequisites: [...safeList(item.prerequisites)],
      outcome: item.outcome,
      planWeek: item.planWeek ?? null,
      color: colors[shortCodes[index]],
    }));
  }

  /**
   * Code court affiché — `C1`, `م1`.
   *
   * Composé ici du préfixe localisé (`REPORT.competency.codePrefix`) et du
   * rang. Le préfixe reste la seule source de vérité du vocabulaire : le
   * rapport ne connaît pas de lettre écrite en dur, et une bascule de langue
   * renomme les axes sans toucher aux données.
   *
   * Le code du référentiel, lui, est conservé dans `CompetencyRow.code` mais
   * n'est pas affiché : ces codes sont longs, hétérogènes d'une matière à
   * l'autre, et débordent des axes de graphiques.
   */
  private shortCode(displayIndex: number): string {
    return `${this.i18n.codePrefix()}${displayIndex}`;
  }

  /* ─────────────────────────────────────────────────────────────────────
     Les deux mesures
     ───────────────────────────────────────────────────────────────────── */

  /** Score du test, repris tel quel : c'est lui, et lui seul, qui donne le palier. */
  private buildScore(subject: SubjectDiagnostic, band: BandRef | null): ScoreView {
    return {
      score: subject.score,
      maxScore: subject.totalPoints,
      markOutOf20: noteSur20(subject.score, subject.totalPoints),
      percentage: subject.scorePercentage,
      target: subject.target ?? null,
      band,
      level: band?.level ?? null,
      passed: subject.passed,
    };
  }

  /**
   * Acquisition des compétences.
   *
   * `acquiredPercentage` vient de l'API — on ne recalcule pas ce qu'elle a déjà
   * calculé, au risque d'afficher deux nombres différents pour la même chose.
   * Les deux autres parts, qu'elle n'envoie pas, sont calculées **sur le même
   * dénominateur** : `competenciesTotal`, qui inclut les compétences non
   * évaluées. C'est la définition de l'API, et rapporter les lacunes aux seules
   * compétences évaluées donnerait un troisième pourcentage incohérent avec le
   * premier.
   */
  private buildAcquisition(subject: SubjectDiagnostic): AcquisitionView {
    const total = subject.competenciesTotal;

    return {
      acquired: subject.competenciesAcquired,
      notAcquired: subject.competenciesNotAcquired,
      notAssessed: subject.competenciesNotAssessed,
      total,
      acquiredPercentage: subject.acquisitionPercentage,
      notAcquiredPercentage: share(subject.competenciesNotAcquired, total),
      notAssessedPercentage: share(subject.competenciesNotAssessed, total),
    };
  }

  /* ─────────────────────────────────────────────────────────────────────
     Paliers
     ───────────────────────────────────────────────────────────────────── */

  /**
   * Palier du catalogue → palier du rapport.
   *
   * Le code du palier est **paramétrable en base** : rien ne garantit qu'il
   * fasse partie des quatre que connaît la maquette. Un code inconnu retombe
   * donc sur `average` — le rapport se dégrade en affichant un palier médian
   * plutôt qu'en refusant de s'afficher, et la comparaison de codes est
   * insensible à la casse pour ne pas dépendre de la façon dont la base a été
   * saisie.
   */
  private toBandRef(band: EvaluationBand): BandRef {
    return {
      code: band.code,
      label: band.label,
      planLabel: band.planLabel,
      minPercentage: band.minPercentage,
      maxPercentage: band.maxPercentage,
      level: BAND_LEVELS[String(band.code).toLowerCase()] ?? 'average',
    };
  }

  /**
   * Palier atteint par une matière.
   *
   * Recherché dans le catalogue par les bornes, pour que le palier annoncé en
   * synthèse soit exactement le segment marqué actif sur l'échelle. Le palier
   * porté par la matière ne sert que de secours, quand le catalogue est vide ou
   * ne couvre pas le score : mieux vaut un palier sans échelle qu'aucun palier.
   */
  private resolveBand(
    subject: SubjectDiagnostic,
    bands: readonly BandRef[],
  ): BandRef | null {
    const fromCatalogue = this.activeBand(bands, subject.scorePercentage);
    if (fromCatalogue) return fromCatalogue;

    return subject.band ? this.toBandRef(subject.band) : null;
  }

  /**
   * Palier dont les bornes contiennent un pourcentage.
   *
   * Les bornes sont **incluses des deux côtés** et exprimées en entiers. Un
   * score fractionnaire — 36,5 % — ne tombe donc dans aucun intervalle : on
   * retient alors le dernier palier commencé, ce qui revient à le rattacher au
   * palier inférieur au lieu de le laisser sans palier.
   */
  private activeBand(
    bands: readonly BandRef[],
    percentage: number,
  ): BandRef | null {
    const exact = bands.find(
      (b) => percentage >= b.minPercentage && percentage <= b.maxPercentage,
    );
    if (exact) return exact;

    let started: BandRef | null = null;
    for (const band of bands) {
      if (percentage >= band.minPercentage) started = band;
    }
    return started;
  }

  /**
   * Échelle complète des paliers.
   *
   * La largeur d'un palier est sa part de l'échelle : son étendue — bornes
   * incluses, donc `max - min + 1` — rapportée à la somme des étendues, puis
   * ramenée sur 100. Sur le catalogue de référence (0-36 / 37-60 / 61-79 /
   * 80-100), les étendues valent 37 / 24 / 19 / 21 et leur somme fait 101, pas
   * 100 : un axe de 0 à 100 compte 101 valeurs entières. La normalisation rend
   * donc 36,63 / 23,76 / 18,81 / 20,79 — les largeurs de la maquette au
   * dixième près, mais dont la somme fait exactement 100. C'est ce que demande
   * le rail : un segment de trop et il déborde de son conteneur.
   *
   * Les largeurs ne sont **pas arrondies** ; les arrondir casserait la somme.
   */
  private buildBandScale(
    bands: readonly BandRef[],
    percentage: number,
  ): BandScaleSegment[] {
    const spans = bands.map((band) => bandSpan(band));
    const total = spans.reduce((sum, span) => sum + span, 0);
    const active = this.activeBand(bands, percentage);

    return bands.map((band, index) => ({
      ...band,
      // Un catalogue d'étendue nulle est une donnée cassée : on rend des
      // segments de largeur nulle plutôt qu'une division par zéro.
      width: total > 0 ? (spans[index] * 100) / total : 0,
      active: band === active,
    }));
  }

  /* ─────────────────────────────────────────────────────────────────────
     Plan de remédiation
     ───────────────────────────────────────────────────────────────────── */

  /**
   * Plan de remédiation, semaine par semaine.
   *
   * **Toutes** les semaines de 1 à `planWeeks` sont émises, y compris vides :
   * la grille du plan aligne autant de cartes que de semaines, et en escamoter
   * une la désaxerait. À l'intérieur d'une semaine, l'ordre est celui du
   * référentiel (`displayIndex`), le même que la table des compétences.
   *
   * Une lacune sans semaine, ou portant une semaine hors de la plage, ne figure
   * pas au plan — elle reste visible dans la colonne des lacunes.
   */
  private buildPlan(gaps: readonly CompetencyRow[], planWeeks: number): PlanWeek[] {
    const weeks: PlanWeek[] = [];

    for (let week = 1; week <= planWeeks; week += 1) {
      weeks.push({
        week,
        competencies: gaps
          .filter((c) => c.planWeek === week)
          .sort((a, b) => a.displayIndex - b.displayIndex),
      });
    }
    return weeks;
  }

  /* ─────────────────────────────────────────────────────────────────────
     Graphiques d'une matière
     ───────────────────────────────────────────────────────────────────── */

  /**
   * Anneau de synthèse : acquises contre non acquises.
   *
   * Il compte des **compétences**, jamais des réponses — c'est l'acquisition
   * qu'il montre, pas le score. Les comptes viennent de l'API, et le vert et le
   * rouge de la palette portent le sens : ce sont les deux seules couleurs de
   * statut du rapport.
   */
  private buildDonut(subject: SubjectDiagnostic, theme: ChartTheme): DonutSlice[] {
    const palette = paletteFor(theme);

    return [
      {
        key: 'acquired',
        label: this.i18n.t('competency.acquiredLabel'),
        value: subject.competenciesAcquired,
        color: palette.good,
      },
      {
        key: 'notAcquired',
        label: this.i18n.t('competency.notAcquiredLabel'),
        value: subject.competenciesNotAcquired,
        color: palette.bad,
      },
    ];
  }

  /** Un rayon par lacune : l'anneau porte la matière du plan, rien d'autre. */
  private buildRing(gaps: readonly CompetencyRow[]): RingSpoke[] {
    return gaps.map((c) => ({ code: c.shortCode, label: c.title, color: c.color }));
  }

  /**
   * Répartition hebdomadaire des lacunes.
   *
   * Les semaines viennent du back, qui ordonne les lacunes selon leurs
   * prérequis — une compétence qui en conditionne une autre passe avant elle.
   * Un simple découpage en quatre l'ignorerait.
   */
  private buildWeekly(gaps: readonly CompetencyRow[]): WeeklyBar[] {
    // `flatMap` plutôt que `filter` puis `map` : le prédicat de type ne se
    // propage pas d'un rappel à l'autre, et `planWeek` resterait nullable.
    return gaps.flatMap((c) =>
      hasWeek(c.planWeek)
        ? [{ code: c.shortCode, label: c.title, week: c.planWeek, color: c.color }]
        : [],
    );
  }

  /**
   * Niveau scolaire de chaque compétence.
   *
   * **Toutes** les compétences y figurent, acquises comprises : le graphique
   * montre à quelle année remonte chaque point du référentiel, pas seulement
   * les lacunes. Le rang est la position du premier niveau dans le référentiel
   * complet, et non son `order` brut — l'axe est une liste de niveaux, pas une
   * échelle numérique. `0` signale une compétence sans niveau rattaché : la vue
   * la place hors de l'axe plutôt que sur son premier cran.
   */
  private buildSchoolLevel(
    competencies: readonly CompetencyRow[],
    ranks: ReadonlyMap<string, number>,
  ): SchoolLevelPoint[] {
    return competencies.map((c) => {
      const level = c.gradeLevels.length > 0 ? c.gradeLevels[0] : null;

      return {
        code: c.shortCode,
        label: c.title,
        rank: level ? (ranks.get(level.code) ?? 0) : 0,
        levelLabel: level ? level.label : '',
        color: c.color,
      };
    });
  }

  /* ─────────────────────────────────────────────────────────────────────
     Niveaux scolaires
     ───────────────────────────────────────────────────────────────────── */

  /**
   * Référentiel des niveaux → rang d'affichage, à partir de 1.
   *
   * La position dans le tableau fait foi : l'API le renvoie déjà trié, et le
   * retrier sur `order` ici ferait diverger le rang du graphique de l'ordre de
   * l'axe, tous deux tirés du même tableau.
   */
  private rankByGradeLevelCode(
    referential: readonly GradeLevelRefItem[],
  ): ReadonlyMap<string, number> {
    const ranks = new Map<string, number>();
    referential.forEach((level, index) => {
      if (!ranks.has(level.code)) ranks.set(level.code, index + 1);
    });
    return ranks;
  }

  /** `isAcquisitionLevel` ne sert pas à l'affichage : le modèle de vue ne le porte pas. */
  private toGradeLevelRef(level: GradeLevelRefItem): GradeLevelRef {
    return { code: level.code, label: level.label, order: level.order };
  }

  /* ─────────────────────────────────────────────────────────────────────
     Blocs d'ensemble
     ───────────────────────────────────────────────────────────────────── */

  /**
   * Tableau des matières et graphique de comparaison.
   *
   * Seule partie du rapport qui regarde au-delà d'une matière, et elle ne
   * compare que des **scores** : les compétences restent propres à leur
   * matière. Les matières non diagnostiquées apparaissent dans le tableau —
   * c'est ce qu'il reste à passer — mais ne pèsent sur aucun total et sortent
   * de la comparaison : opposer une matière non passée à un objectif se
   * lirait comme un échec.
   */
  private buildCrossSubject(
    report: DiagnosticReport,
    subjects: readonly SubjectReportView[],
  ): CrossSubjectView {
    const diagnosed = subjects.filter((s) => s.hasDiagnostic);
    const best = this.bestSubject(diagnosed);

    const subjectRows: SubjectRow[] = subjects.map((s) => ({
      subjectId: s.subjectId,
      subjectName: s.subjectName,
      hasDiagnostic: s.hasDiagnostic,
      score: s.score.score,
      maxScore: s.score.maxScore,
      percentage: s.score.percentage,
      band: s.score.band,
      level: s.score.level,
      highlighted: s === best,
    }));

    const comparison: ComparisonRow[] = diagnosed.map((s) => ({
      subjectId: s.subjectId,
      label: s.subjectName,
      student: s.score.percentage,
      target: s.score.target,
    }));

    return {
      subjectRows,
      comparison,
      // Moyenne de l'API sur les matières diagnostiquées : la recalculer ici
      // donnerait un second nombre pour la même chose.
      overallScorePercentage: report.overallScorePercentage,
      averageTarget: this.averageTarget(diagnosed),
      totalCompetenciesAcquired: report.totalCompetenciesAcquired,
      totalCompetenciesAssessed: report.totalCompetenciesAssessed,
      diagnosedCount: diagnosed.length,
    };
  }

  /**
   * Matière la mieux réussie, mise en avant dans le tableau.
   *
   * Sur les seules matières diagnostiquées, et à égalité la première dans
   * l'ordre de l'API — la comparaison stricte laisse le premier en place,
   * plutôt que de faire changer la ligne surlignée à chaque rendu.
   */
  private bestSubject(
    diagnosed: readonly SubjectReportView[],
  ): SubjectReportView | null {
    return diagnosed.reduce<SubjectReportView | null>(
      (top, s) => (top === null || s.score.percentage > top.score.percentage ? s : top),
      null,
    );
  }

  /**
   * Moyenne des objectifs renseignés.
   *
   * Arrondie, parce qu'un objectif est une barre affichée en pourcentage
   * entier. `null` quand aucune matière diagnostiquée n'en porte : afficher 0
   * ferait croire à un objectif nul.
   */
  private averageTarget(diagnosed: readonly SubjectReportView[]): number | null {
    const targets = diagnosed
      .map((s) => s.score.target)
      .filter((t): t is number => t !== null);

    if (targets.length === 0) return null;
    return Math.round(targets.reduce((sum, t) => sum + t, 0) / targets.length);
  }

  /* ─────────────────────────────────────────────────────────────────────
     En-tête et sélecteur
     ───────────────────────────────────────────────────────────────────── */

  private buildHeader(
    report: DiagnosticReport,
    scope: ReportScope,
    subjects: readonly SubjectReportView[],
  ): ReportHeaderView {
    const displayed = scope === 'subject' ? (subjects[0] ?? null) : null;
    const diagnosed = subjects.filter((s) => s.hasDiagnostic);

    // Sur une matière, le test affiché est le sien. Sur plusieurs, il n'y a pas
    // un test mais plusieurs : on garde la séance diagnostiquée la plus
    // récente, seul repère qui ait un sens en tête d'un rapport d'ensemble.
    const reference = displayed ?? this.mostRecent(diagnosed);

    return {
      studentName: report.studentName,
      initials: initialsOf(report.studentName),
      studentCode: report.studentCode ?? null,
      learningPathTitle: report.learningPathTitle ?? null,

      subjectLabel: displayed ? displayed.subjectName : this.allSubjectsLabel(),
      testTitle: reference?.testTitle ?? null,
      completedAt: reference?.completedAt ?? null,
      // Sur plusieurs matières, la durée du document est celle de toutes les
      // séances qu'il couvre, et non celle de la plus récente : l'en-tête
      // annonçait « 5 minutes » au-dessus d'un panneau qui en affichait 21,
      // sans qu'aucun des deux soit faux — ils ne parlaient pas de la même
      // épreuve.
      durationMinutes: displayed
        ? displayed.durationMinutes
        : sommeDesDurees(diagnosed),
      durationIsTotal: !displayed,
      // Séances couvertes **par le document**, pas par le dossier de l'élève :
      // en portée `subject` il n'y en a au plus qu'une, et annoncer les trois
      // du rapport complet au-dessus d'un panneau unique serait faux.
      sessionCount: diagnosed.length,
    };
  }

  /** Séance diagnostiquée la plus récente ; les dates ISO se comparent en texte. */
  private mostRecent(
    diagnosed: readonly SubjectReportView[],
  ): SubjectReportView | null {
    return diagnosed.reduce<SubjectReportView | null>((latest, s) => {
      if (s.completedAt === null) return latest;
      if (latest === null || latest.completedAt === null) return s;
      return s.completedAt > latest.completedAt ? s : latest;
    }, null);
  }

  /**
   * Mention « toutes les matières ».
   *
   * Prise dans le bundle applicatif (`DIAGNOSTIC_REPORT.ALL_SUBJECTS`) et non
   * dans celui du rapport : `REPORT.header` n'a pas de clé équivalente dans les
   * trois langues, et inventer `REPORT.header.allSubjects` afficherait la clé
   * brute en tête du document. La traduction existe déjà côté application, dans
   * ar/fr/en.
   */
  private allSubjectsLabel(): string {
    const label: unknown = this.translate.instant(ALL_SUBJECTS_KEY);
    return typeof label === 'string' ? label : ALL_SUBJECTS_KEY;
  }

  /**
   * Choix de la barre latérale : « toutes les matières », puis chaque matière.
   *
   * Construit sur la charge utile entière et non sur la portée : la barre
   * latérale sert justement à sortir de la matière affichée. Une matière non
   * diagnostiquée n'a ni pourcentage ni palier — pas de pastille plutôt qu'une
   * pastille à zéro.
   */
  private buildSubjectOptions(
    report: DiagnosticReport,
    subjects: readonly SubjectDiagnostic[],
    bands: readonly BandRef[],
  ): SubjectOption[] {
    const all: SubjectOption = {
      id: null,
      label: this.allSubjectsLabel(),
      hasDiagnostic: true,
      percentage: report.overallScorePercentage,
      // Pas de palier d'ensemble : le palier s'attribue matière par matière.
      level: null,
    };

    return [
      all,
      ...subjects.map((s) => ({
        id: s.subjectId,
        label: s.subjectName,
        hasDiagnostic: s.hasDiagnostic,
        percentage: s.hasDiagnostic ? s.scorePercentage : null,
        level: s.hasDiagnostic ? (this.resolveBand(s, bands)?.level ?? null) : null,
      })),
    ];
  }
}

/* ─────────────────────────────────────────────────────────────────────────
   Rouages
   ───────────────────────────────────────────────────────────────────────── */

/** Clé applicative de la mention « toutes les matières ». */
const ALL_SUBJECTS_KEY = 'DIAGNOSTIC_REPORT.ALL_SUBJECTS';

/**
 * Code de palier → vocabulaire visuel de la maquette.
 *
 * Les codes sont paramétrables en base : cette table dit ce que la maquette
 * sait dessiner, pas ce que la base contient. Tout code absent est traité en
 * `average` par {@link ReportViewBuilder.toBandRef}.
 */
const BAND_LEVELS: Readonly<Record<string, ReportLevel>> = {
  remediation: 'weak',
  improvement: 'average',
  development: 'good',
  talent: 'excellent',
};

/** Ancre de navigation d'un panneau de matière. */
function anchorFor(subjectId: number): string {
  return `subject-${subjectId}`;
}

/**
 * Lecture défensive d'un tableau de l'API.
 *
 * Le contrat les déclare obligatoires, la sérialisation renvoie parfois `null`
 * pour une collection vide. Le rapport doit s'afficher quand même : une section
 * vide se lit, une exception non.
 */
function safeList<T>(value: readonly T[] | null | undefined): readonly T[] {
  return Array.isArray(value) ? value : [];
}

/** Part en pourcentage, `0` si le dénominateur est nul. */
function share(part: number, whole: number): number {
  return whole > 0 ? (part * 100) / whole : 0;
}

/** Étendue d'un palier, bornes incluses. */
function bandSpan(band: BandRef): number {
  return Math.max(0, band.maxPercentage - band.minPercentage + 1);
}

/** Semaine de plan exploitable : un entier à partir de 1. */
function hasWeek(week: number | null): week is number {
  return week !== null && Number.isFinite(week) && week >= 1;
}

/**
 * Nombre de semaines du plan.
 *
 * Une valeur absente ou aberrante donne un plan vide plutôt qu'une boucle sur
 * un nombre négatif ou fractionnaire.
 */
function normalizeWeekCount(weeks: number): number {
  return Number.isFinite(weeks) && weeks > 0 ? Math.floor(weeks) : 0;
}

/**
 * Initiales de repli, quand aucune photo n'est disponible.
 *
 * Première lettre des deux premiers mots. `Array.from` plutôt qu'un index de
 * chaîne pour ne pas couper un caractère en deux — les noms arabes restent
 * dans le plan multilingue de base, mais la règle ne coûte rien et vaut pour
 * tout nom. La mise en majuscule est sans effet en arabe, qui n'a pas de casse.
 * Un nom vide rend une chaîne vide : la vue affiche alors la silhouette par
 * défaut plutôt qu'une pastille avec un caractère parasite.
 */
function initialsOf(name: string): string {
  return (name ?? '')
    .split(/\s+/)
    .filter((part) => part.length > 0)
    .slice(0, 2)
    .map((part) => Array.from(part)[0].toUpperCase())
    .join('');
}
